import { UI, HStack, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { OperationType } from '../../../../../../../../../components/OperationType';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useEquitySellAssetController } from '../../../../../hooks/products/EquitySell';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';

import { EquityItemSelectedTypes } from '.';

export const EquityItemSelectedMobile: React.FC<EquityItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  hideLateralBar = false,
}) => {
  const id = offer.productIdNormalized;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    availableAmount,
  } = useEquitySellAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === priceItem || !priceItem) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: priceItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceItem]);

  useEffect(() => {
    if (offer.orderQuantity === amountItem || !amountItem) {
      return;
    }
    const updatedOffer = { ...offer, orderQuantity: amountItem || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountItem]);

  const middleInfos = [
    {
      label: 'Operação',
      value: <OperationType title="Venda" />,
    },
    {
      label: 'Validade da ordem',
      value: <UI.Text color="neutral.dark">Hoje</UI.Text>,
    },
    {
      label: 'Tipo da ordem',
      value: <UI.Text color="neutral.dark">Limitada</UI.Text>,
    },
  ];

  return (
    <VStack space="stack.xxxs" w="full">
      {!hideLateralBar && (
        <LateralBar
          backgroundColor="product.rendavariavelsale"
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
      )}
      <HStack justifyContent="space-between" flex={1} alignItems="center" py="stack.xxxs">
        <AssetTitleWithImage
          image={offer.imageNormalized || ''}
          title={offer.productNameNormalized || ''}
          subtitle={offer.symbolNormalized || ''}
        />
        <AssetValueWithPercentage
          value={offer.lastPriceNormalized}
          percentage={offer.priceChangeTaxValueNormalized}
          iconColor={offer.priceChangeTaxColorNormalized}
          updateDate={offer.tradeDateNormalized}
        />
      </HStack>
      <VStack space="stack.nano" flexGrow={1}>
        {middleInfos.map(({ label, value }) => (
          <HStack space="inline.quarck" justifyContent="space-between" key={label}>
            <UI.Text color="neutral.black" variant="semibold">
              {label}
            </UI.Text>
            {value}
          </HStack>
        ))}
      </VStack>

      <VStack space={Spacing['stack.quarck']} flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Quantidade
        </UI.Text>
        <VStack flex={1}>
          <UI.InputStepper
            stepperValue={cautionStepper}
            max={availableAmount}
            min={fractionalLotSize}
            name={`equity-amount-order-input${id}`}
            keyboardType="numeric"
            defaultValue={amountItem}
            onChange={handleChangeAmountItem}
            showErrorMessage={false}
            onFocus={() => setIsFocusedAmountInput(true)}
            onBlur={() => setIsFocusedAmountInput(false)}
            {...(isFocusedAmountInput &&
              offer.saleAvailableAmount && {
                helpText: `Quantidade disp.: ${offer.saleAvailableAmount}`,
              })}
            {...(totalPriceIsInvalid && {
              customError: 'Quantidade inválida',
            })}
          />
        </VStack>
      </VStack>

      <VStack space={Spacing['stack.quarck']} flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Preço por ativo
        </UI.Text>
        <VStack flex={1}>
          <UI.InputStepper
            stepperValue={0.01}
            max={1000000}
            min={0.01}
            keyboardType="numeric"
            ignoreStepperDivision
            maskOption="money"
            name={`equity-amount-price${id}`}
            defaultValue={priceItem || 0.01}
            onChange={handleChangePriceItem}
            showErrorMessage={false}
            {...(totalPriceIsInvalid && {
              customError: 'Valor inválido',
            })}
          />
        </VStack>
      </VStack>

      <HStack space="inline.quarck" justifyContent="space-between">
        <UI.Text color="neutral.black" variant="semibold">
          Total estimado
        </UI.Text>
        <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
      </HStack>
    </VStack>
  );
};
